import { Component, OnInit, OnDestroy } from '@angular/core';
import { ExpenseVouchersService } from '../../services/expense-vouchers.service';
import { LoadingService } from '../../services/loading.service';
import { ToastService } from '../../services/toast.service';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-expense-receipts',
  templateUrl: './expense-receipts.component.html',
  styleUrls: ['./expense-receipts.component.scss']
})
export class ExpenseReceiptsComponent implements OnInit, OnDestroy {
  expenseVouchers: any[] = [];
  filteredVouchers: any[] = [];

  // Debounce
  private filterSubject = new Subject<void>();
  private destroy$ = new Subject<void>();

  // Filtros
  searchTerm: string = '';
  filterId: number | null = null;
  selectedBank: any = null; // 🔄 Cambiado para p-dropdown
  
  // 🆕 Filtros de fecha
  startDate: Date | null = null;
  endDate: Date | null = null;
  dateType: string = 'Creation'; // 'Creation' o 'Payment'
  dateTypeOptions: any[] = [
    { label: 'Fecha de Creación', value: 'Creation' },
    { label: 'Fecha de Pago', value: 'Payment' }
  ];
  
  // 🆕 Ordenamiento
  sortOrder: 'asc' | 'desc' = 'desc'; // Por defecto descendente (más recientes primero)

  // 🆕 Paginación
  pageNumber: number = 1;
  pageSize: number = 20;
  totalRecords: number = 0;
  paginatedVouchers: any[] = [];

  // Métricas
  totalExpenses: number = 0;
  monthlyAverage: number = 0;
  lastMonthTotal: number = 0;
  banks: any = [];

  // Filtro de anulación
  selectedCancellationFilter: string = 'active';

  // Modal de anulación
  showConfirmationModal = false;
  cancellationReason = '';
  selectedVoucherForCancellation: any = null;

  constructor(
    private expenseVoucherService: ExpenseVouchersService,
    private loadingService: LoadingService,
    private toastService: ToastService
  ) { }

  ngOnInit(): void {
    this.filterSubject.pipe(
      debounceTime(400),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.pageNumber = 1;
      this.loadData();
    });

    this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadData(): void {
    this.loadingService.show();
    
    // 🆕 USAR SERVICIO PAGINADO DEL LADO SERVIDOR
    const parameters: any = {
      pageNumber: this.pageNumber,
      pageSize: this.pageSize,
      searchTerm: this.searchTerm,
      sortBy: 'Id',
      sortOrder: this.sortOrder,
      startDate: this.startDate,
      endDate: this.endDate,
      dateType: this.dateType,
      cancellationFilter: this.selectedCancellationFilter,
      bankId: this.selectedBank
    };

    if (this.filterId) parameters.filterId = this.filterId;

    this.expenseVoucherService.getExpenseVouchersPaged(parameters).subscribe({
      next: (response) => {
        this.expenseVouchers = response.data || [];
        this.totalRecords = response.totalRecords || 0;
        this.paginatedVouchers = this.expenseVouchers; // Ya viene paginado del servidor
        
        this.processData();
        this.loadingService.hide();
      },
      error: (error) => {
        console.error('Error al cargar comprobantes de egreso paginados:', error);
        this.toastService.showToast('error', 'Error al cargar los datos');
        this.loadingService.hide();
      }
    });
  }

  private processData(): void {
    // Calcular métricas
    this.totalExpenses = this.expenseVouchers.reduce((sum, v) => sum + v.totalToPay, 0);
    this.lastMonthTotal = this.calculateLastMonthTotal();

    // Obtener bancos únicos - 🔧 Convertir a array para p-dropdown
    this.banks = Array.from(new Map(
      this.expenseVouchers.map(
        (v: any) => [v.bank.id, v.bank]
      )
    ).values());
  }




  private calculateLastMonthTotal(): number {
    const lastMonth = new Date();
    lastMonth.setMonth(lastMonth.getMonth() - 1);
    return this.expenseVouchers
      .filter(v => new Date(v.createdAt) >= lastMonth)
      .reduce((sum, v) => sum + v.totalToPay, 0);
  }

  // 🆕 Método para obtener documento del tercero
  getThirdPartyDocument(voucher: any): string {
    if (voucher.thirdParties) {
      return `${voucher.thirdParties.typeDocument || ''} ${voucher.thirdParties.documentId || ''}`.trim();
    }
    return 'N/A';
  }

  // 🆕 Método para cambio de página
  onPageChange(event: any): void {
    this.pageNumber = event.page + 1;
    this.pageSize = event.rows;
    this.loadData();
  }

  // Método para cambio de filtros (con debounce)
  onFilterChange(): void {
    this.filterSubject.next();
  }

  // 🆕 Método para cambiar ordenamiento
  toggleSortOrder(): void {
    this.sortOrder = this.sortOrder === 'desc' ? 'asc' : 'desc';
    this.loadData();
  }


  // Cambio de filtro de anulación
  onCancellationFilterChange(): void {
    this.loadData();
  }

  // Anulación
  showCancelModal(voucher: any): void {
    if (voucher.isCancelled) {
      this.toastService.showToast('warning', 'Este comprobante ya está anulado');
      return;
    }
    this.selectedVoucherForCancellation = voucher;
    this.cancellationReason = '';
    this.showConfirmationModal = true;
  }

  async onConfirmCancellation(): Promise<void> {
    if (!this.cancellationReason?.trim()) {
      this.toastService.showToast('error', 'Debe especificar el motivo de anulación');
      return;
    }
    if (!this.selectedVoucherForCancellation) return;

    try {
      this.loadingService.show();
      await this.expenseVoucherService
        .cancelExpenseVoucher(this.selectedVoucherForCancellation.id, this.cancellationReason.trim())
        .toPromise();
      this.toastService.showToast('success', '✅ Comprobante de egreso anulado exitosamente');
      this.closeConfirmationModal();
      this.loadData();
    } catch (error: any) {
      console.error('Error al anular comprobante de egreso:', error);
      const errorMessage = error?.error?.errorMessage || error?.error || 'Error al anular el comprobante de egreso';
      this.toastService.showToast('error', errorMessage);
    } finally {
      this.loadingService.hide();
    }
  }

  onCancelCancellation(): void {
    this.closeConfirmationModal();
  }

  private closeConfirmationModal(): void {
    this.showConfirmationModal = false;
    this.selectedVoucherForCancellation = null;
    this.cancellationReason = '';
  }

  // 🆕 MÉTODOS DE DESCARGA DE PDF
  isDownloadingPdf: { [key: number]: boolean } = {};

  downloadPdf(voucher: any): void {
    if (this.isDownloadingPdf[voucher.id]) return;

    this.isDownloadingPdf[voucher.id] = true;
    this.expenseVoucherService.downloadPdf(voucher.id).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `ComprobanteEgreso_${voucher.id}.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
        this.isDownloadingPdf[voucher.id] = false;
        this.toastService.showToast('success', 'PDF descargado correctamente');
      },
      error: (error) => {
        console.error('Error downloading PDF:', error);
        this.isDownloadingPdf[voucher.id] = false;
        this.toastService.showToast('error', 'Error al descargar el PDF');
      }
    });
  }
}
